import { ModalDialog } from 'components/ModalDialog';
import type { BimPriceLevelEnum, ContractStatusEnum, ProjectStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { cnMixSpace, Loader, Tabs } from 'uikit';

import type { ColDef, RowClassRules } from 'ag-grid-community';
import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { BimModalTitleWithPriceLevel } from '../../../BimCommonComponents/BimModalTitleWithPriceLevel';
import { BimWorkOrderActKc2PriceDetails } from '../../BimWorkOrderActsKc2/BimWorkOrderActKc2PriceDetails';
import { BimWorkOrderActKc2TotalDetails } from '../../BimWorkOrderActsKc2/BimWorkOrderActKc2TotalDetails';
import { useBimActsKc2DetailsData } from '../../BimWorkOrderActsKc2/hooks';
import { ActTotalDetailsType } from '../../BimWorkOrderActsKc2/types';

import Styles from './BimWorkOrderActsKc2.module.css';

interface IBimWorkOrderActsKc2 {
    onClose: () => void;
    actKc2Smr: any;
    defaultColDef?: ColDef;
    rowClassRules?: RowClassRules;
    styleClass?: string;
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
    statusProject?: ProjectStatusEnum;
    isActionDisabled?: boolean;
    isReadOnly?: boolean;
    priceLevel: BimPriceLevelEnum | undefined;
}

const getLabel = R.prop('name');

const tabsItems = [
    { id: ActTotalDetailsType.Totals, name: 'Итоги акта' },
    { id: ActTotalDetailsType.Prices, name: 'Состав акта' },
];

export const BimWorkOrderActsKc2Details: React.FC<IBimWorkOrderActsKc2> = (props) => {
    const {
        actKc2Smr,
        onClose,
        defaultColDef,
        styleClass,
        workOrderStatus,
        contractStatus,
        statusProject,
        isActionDisabled = false,
        isReadOnly = false,
        priceLevel,
    } = props;

    const {
        isDetailsOpened,
        detailsTitle,
        checkedActId,
        isTotalsLoading,
        isPricesLoading,
        refreshActsKc2TotalsTable,
        setCheckedActForDetails,
        totalsData,
        pricesData,
        actDetailsType,
        setActDetailsType,
        localPriceLevel,
        closeDetails,
    } = useBimActsKc2DetailsData(priceLevel, isReadOnly);

    const paramsDetailsData = useMemo(
        () => ({
            id: actKc2Smr?.actId,
            number: actKc2Smr?.number,
        }),
        [actKc2Smr?.actId, actKc2Smr?.number],
    );

    useEffect(() => {
        setCheckedActForDetails(paramsDetailsData as any);
    }, [paramsDetailsData, actKc2Smr, setCheckedActForDetails]);

    const closeModalHandler = useCallback(() => {
        if (detailsTitle) {
            onClose();
            closeDetails();
        }
    }, [closeDetails, detailsTitle, onClose]);

    const bodyModalContainerHeight = isDetailsOpened ? '' : '500px';

    const makeRefreshTotalsData = useCallback(() => {
        refreshActsKc2TotalsTable();
    }, [refreshActsKc2TotalsTable]);

    const onTabChange = useCallback(
        ({ value }) => {
            setActDetailsType?.(value.id);
        },
        [setActDetailsType],
    );

    const isCommitting = useMemo(() => isTotalsLoading || isPricesLoading, [isPricesLoading, isTotalsLoading]);

    const tabValue = useMemo(() => tabsItems.find((i) => i.id === actDetailsType), [actDetailsType]);

    const modalTitle = detailsTitle || '';

    return (
        <ModalDialog
            isOpen
            title={<BimModalTitleWithPriceLevel priceLevel={priceLevel} modalTitle={modalTitle} />}
            onClose={closeModalHandler}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            withBottomButtons={false}
            style={{ width: '100vw' }}
            bodyMinHeight="500px"
            bodyMaxHeight={bodyModalContainerHeight}
            bodyOverflow="hidden"
        >
            <div className={cnMixSpace({ pV: 's', pH: 'l' })}>
                <Tabs
                    items={tabsItems}
                    getItemLabel={getLabel}
                    onChange={onTabChange}
                    value={tabValue}
                    style={{ marginBottom: 20 }}
                />
                {actDetailsType === ActTotalDetailsType.Totals && (
                    <BimWorkOrderActKc2TotalDetails
                        actKC2Id={checkedActId}
                        actKC2Details={totalsData}
                        styleClass={styleClass}
                        defaultColDef={defaultColDef}
                        onSuccess={makeRefreshTotalsData}
                        isLoading={isTotalsLoading}
                        contractStatus={contractStatus}
                        workOrderStatus={workOrderStatus}
                        statusProject={statusProject}
                        isActionDisabled={isActionDisabled}
                        isReadOnly={isReadOnly}
                    />
                )}
                {actDetailsType === ActTotalDetailsType.Prices && localPriceLevel && (
                    <BimWorkOrderActKc2PriceDetails
                        pricesData={pricesData}
                        styleClass={styleClass}
                        defaultColDef={defaultColDef}
                    />
                )}
                {isCommitting && (
                    <div className={Styles.overlayLoader}>
                        <Loader />
                    </div>
                )}
            </div>
        </ModalDialog>
    );
};

BimWorkOrderActsKc2Details.displayName = 'BimWorkOrderActsKc2Details';
